import type { ErrorEvent, StackFrame } from '@sentry/react';

function getErrorMessage(event: ErrorEvent): string {
  return event.exception?.values?.[0]?.value ?? event.message ?? '';
}

function getStackFrames(event: ErrorEvent): StackFrame[] {
  return event.exception?.values?.[0]?.stacktrace?.frames ?? [];
}

export function filterSentryEvent(event: ErrorEvent): ErrorEvent | null {
  const errorMessage = getErrorMessage(event);

  // Browser/extension-injected script trying to use the Chrome extension messaging API.
  // Shows up as unhandled promise rejection on some iOS/Safari contexts. Not our code.
  if (/runtime\.sendMessage\(\).*Tab not found/i.test(errorMessage)) return null;

  // Amplitude Session Replay sometimes can't persist to IndexedDB in iOS in-app browsers
  // (e.g. Facebook/Instagram) or after a user clears site storage. Not a funnel bug.
  if (
    /Failed to store session replay events in IndexedDB/i.test(errorMessage) ||
    /Database deleted by request of the user/i.test(errorMessage)
  ) {
    return null;
  }

  // Facebook/Instagram iOS in-app browsers expose window.webkit as {} without
  // messageHandlers — third-party scripts probing for it throw here. Not our bug.
  if (/webkit\.messageHandlers/i.test(errorMessage)) return null;

  // Benign layout-thrash warning, not an actual error.
  if (/ResizeObserver loop/i.test(errorMessage)) return null;

  // Fetch / promise aborted because the user navigated away, switched tabs, or the
  // in-app browser (e.g. Facebook iOS) tore down the page. Not a product defect.
  const exceptionValues = event.exception?.values ?? [];
  const isAbort =
    /AbortError|^The operation was aborted\.?$/i.test(errorMessage) ||
    exceptionValues.some(
      (v) =>
        v.type === 'AbortError' ||
        /AbortError|The operation was aborted/i.test(v.value ?? '')
    );
  if (isAbort) return null;

  const stackFrames = getStackFrames(event);
  const stackFilenames = stackFrames.map((f) => f.filename ?? '').join(' ');

  // Pinterest Tag (loaded via GTM) calls ct.pinterest.com — fails under ad blockers,
  // strict WebViews, or DNS blocks. Crashes in pinimg.com script, not app source.
  if (
    /Failed to fetch \(ct\.pinterest\.com\)/i.test(errorMessage) ||
    /ct\.pinterest\.com/i.test(errorMessage) ||
    /pinimg\.com\/ct\//i.test(stackFilenames)
  ) {
    return null;
  }

  // Android in-app browser bridge teardown (e.g. Facebook IAB) can emit this error
  // during beforeunload when the Java object backing keyboard logging is destroyed.
  const isAndroidIabBridgeTeardown =
    /Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone/i.test(
      errorMessage
    ) &&
    /iabjs:\/\/navigation_performance_logger_android/i.test(stackFilenames);
  if (isAndroidIabBridgeTeardown) return null;

  // Some iOS in-app browser contexts inject globals that are not present in
  // our page runtime and throw at document root script execution.
  const isMissingInjectedGlobalG =
    /Can't find variable:\s*_G/i.test(errorMessage) &&
    stackFrames.some((frame) =>
      /^https:\/\/walkmore\.move-club\.co\/?$/i.test(frame.filename ?? '')
    );
  if (isMissingInjectedGlobalG) return null;

  // Errors from browser extensions injecting scripts into the page.
  const hasExtensionFrame = stackFrames.some((frame) =>
    /^(chrome-extension|moz-extension):\/\//i.test(frame.filename ?? '')
  );
  if (hasExtensionFrame) return null;

  return event;
}
